/**
 * Nominatim geocoding service
 * Uses OpenStreetMap Nominatim API for location search
 */

const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';

const LOCALITY_KEYS = [
    'suburb',
    'neighbourhood',
    'city',
    'town',
    'village',
    'hamlet',
    'municipality',
    'county',
    'district'
];

const STATE_KEYS = ['state', 'province', 'region', 'state_district'];

function buildSearchUrl(query, { limit = 5, addressdetails = 1 } = {}) {
    const params = new URLSearchParams({
        format: 'json',
        q: query,
        limit: String(limit),
        addressdetails: String(addressdetails)
    });

    return `${NOMINATIM_SEARCH_URL}?${params.toString()}`;
}

function normalizeText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function pickFirst(address, keys) {
    if (!address || typeof address !== 'object') {
        return '';
    }

    for (const key of keys) {
        const value = normalizeText(address[key]);
        if (value) {
            return value;
        }
    }

    return '';
}

function buildPrimaryName(result) {
    const address = result?.address;
    const normalizedType = normalizeText(result?.type).toLowerCase();
    const roadName = pickFirst(address, ['road', 'residential', 'pedestrian', 'footway', 'cycleway']);
    const houseNumber = normalizeText(address?.house_number);
    const streetAddressLabel =
        houseNumber && roadName
            ? `${houseNumber} ${roadName}`
            : normalizedType === 'house'
              ? roadName
              : '';

    const candidates = [
        normalizedType === 'house' ? streetAddressLabel : '',
        normalizeText(result?.name),
        streetAddressLabel,
        pickFirst(address, [
            'aeroway',
            'railway',
            'road',
            'peak',
            'city',
            'town',
            'village',
            'municipality'
        ]),
        normalizeText(result?.display_name).split(',')[0]?.trim()
    ];

    return candidates.find(Boolean) || 'Unnamed location';
}

function dedupeParts(parts, primaryName) {
    const seen = new Set();
    const normalizedPrimaryName = normalizeText(primaryName).toLowerCase();

    return parts.filter((part) => {
        const normalizedPart = normalizeText(part).toLowerCase();
        if (!normalizedPart || normalizedPart === normalizedPrimaryName) {
            return false;
        }

        if (seen.has(normalizedPart)) {
            return false;
        }

        seen.add(normalizedPart);
        return true;
    });
}

function buildSecondaryText(result, primaryName) {
    const address = result?.address;
    const displayName = normalizeText(result?.display_name);
    const locality = pickFirst(address, LOCALITY_KEYS);
    const state = pickFirst(address, STATE_KEYS);
    const country = normalizeText(address?.country);

    let parts = dedupeParts([locality, state, country], primaryName);
    if (parts.length === 0 && displayName) {
        parts = dedupeParts(
            displayName
                .split(',')
                .map((part) => part.trim())
                .slice(1, 4),
            primaryName
        );
    }

    return parts.join(', ');
}

function normalizeSearchResult(result) {
    const latitude = Number.parseFloat(result?.lat);
    const longitude = Number.parseFloat(result?.lon);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
    }

    const name = buildPrimaryName(result);

    return {
        id: `search-${result.place_id}`,
        name: normalizeText(result?.display_name),
        primaryName: name,
        secondaryText: buildSecondaryText(result, name),
        latitude,
        longitude,
        address: normalizeText(result?.display_name)
    };
}

/**
 * Search for locations using Nominatim API
 * @param {string} query - Search query (place name, address, etc.)
 * @returns {Promise<Array>} Array of location results
 */
export async function searchLocation(query) {
    if (!query || !query.trim()) {
        throw new Error('Search query is required');
    }

    const url = buildSearchUrl(query.trim());

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                // Browser will send its own User-Agent
                // Referer is automatically set by the browser
            }
        });

        if (!response.ok) {
            throw new Error(`Search request failed with status ${response.status}`);
        }

        const results = await response.json();
        return results;

    } catch (error) {
        console.error('Location search error:', error);
        throw new Error(`Failed to search location: ${error.message}`);
    }
}

/**
 * Search locations and return normalized results.
 * @param {string} query - Search query (place name, address, etc.)
 * @param {Object} [options]
 * @param {number} [options.limit=5]
 * @returns {Promise<Array<{id: string, name: string, primaryName: string, secondaryText: string, latitude: number, longitude: number, address: string}>>}
 */
export async function searchLocationsNormalized(query, options = {}) {
    if (!query || query.trim().length < 3) {
        return [];
    }

    const trimmedQuery = query.trim();
    const url = buildSearchUrl(trimmedQuery, options);

    try {
        const response = await fetch(url, {
            method: 'GET'
        });

        if (!response.ok) {
            throw new Error(`Search request failed with status ${response.status}`);
        }

        const results = await response.json();
        return results.map(normalizeSearchResult).filter(Boolean);
    } catch (error) {
        console.error('Location search error:', error);
        throw new Error(`Failed to search location: ${error.message}`);
    }
}

export default {
    searchLocation,
    searchLocationsNormalized
};
